import { useState } from 'react';
import { getEmbedUrl } from '../../data/trainingSessionHelpers';
import { 
  ChevronLeft, 
  ChevronRight, 
  SkipForward, 
  CheckCircle,
  X,
  Play
} from 'lucide-react';
import { Button } from '../ui';
import { cn } from '../../utils/cn';

export function WarmupPlayer({ 
  warmupData, 
  currentCategoryIndex,
  currentExerciseIndex,
  onExerciseComplete,
  onSkipCategory,
  onWarmupComplete,
  onExit
}) {
  const [showVideo, setShowVideo] = useState(true);

  const categories = warmupData?.categories || [];
  const currentCategory = categories[currentCategoryIndex];
  const currentExercise = currentCategory?.exercises[currentExerciseIndex];

  // Calculate total exercises and current position
  const totalExercises = categories.reduce((sum, cat) => sum + cat.exercises.length, 0);
  const exercisesBefore = categories
    .slice(0, currentCategoryIndex)
    .reduce((sum, cat) => sum + cat.exercises.length, 0);
  const currentOverallIndex = exercisesBefore + currentExerciseIndex;
  const progress = ((currentOverallIndex + 1) / totalExercises) * 100;

  // Check if this is the last exercise
  const isLastExercise = 
    currentCategoryIndex === categories.length - 1 && 
    currentExerciseIndex === currentCategory?.exercises.length - 1;

  // Check if this is the last exercise in the current category
  const isLastInCategory = currentExerciseIndex === currentCategory?.exercises.length - 1;

  const handleNext = () => {
    if (isLastExercise) {
      onWarmupComplete();
    } else if (isLastInCategory) {
      // Move to next category
      onExerciseComplete(currentCategoryIndex + 1, 0);
    } else {
      // Move to next exercise in same category
      onExerciseComplete(currentCategoryIndex, currentExerciseIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentExerciseIndex > 0) {
      // Previous exercise in same category
      onExerciseComplete(currentCategoryIndex, currentExerciseIndex - 1);
    } else if (currentCategoryIndex > 0) {
      // Last exercise of previous category
      const prevCategory = categories[currentCategoryIndex - 1];
      onExerciseComplete(currentCategoryIndex - 1, prevCategory.exercises.length - 1);
    }
  };

  const handleSkipCategory = () => {
    if (currentCategoryIndex < categories.length - 1) {
      // Skip to next category
      onSkipCategory(currentCategoryIndex + 1, 0);
    } else {
      // No more categories, complete warmup
      onWarmupComplete();
    }
  };

  if (!currentExercise) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No exercises available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
          <span>{currentCategory.name}</span>
          <span>{currentOverallIndex + 1} of {totalExercises}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="h-2 rounded-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Category Pills */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 overflow-x-auto">
        <div className="flex gap-2">
          {categories.map((cat, idx) => {
            const isActive = idx === currentCategoryIndex;
            const isCompleted = idx < currentCategoryIndex;
            
            return (
              <button
                key={cat.id}
                onClick={() => onExerciseComplete(idx, 0)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors",
                  isActive && "bg-primary text-white",
                  isCompleted && "bg-green-100 text-green-700",
                  !isActive && !isCompleted && "bg-gray-100 text-gray-500"
                )}
              >
                {isCompleted && <CheckCircle className="h-3 w-3 inline mr-1" />}
                {cat.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Exercise Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 max-w-2xl mx-auto">
          {/* Exercise Title */}
          <h2 className="text-xl font-bold text-gray-900 mb-1">
            {currentExercise.name}
          </h2>
          <p className="text-primary font-medium mb-4">
            {currentExercise.documentation}
          </p>

          {/* Video */}
          <div className="bg-gray-900 rounded-xl overflow-hidden mb-4 aspect-video">
            {showVideo ? (
              <iframe
                src={getEmbedUrl(currentExercise.video)}
                title={currentExercise.name}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <button
                onClick={() => setShowVideo(true)}
                className="w-full h-full flex items-center justify-center"
              >
                <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center">
                  <Play className="h-8 w-8 text-white ml-1" />
                </div>
              </button>
            )}
          </div>

          {/* Exercise Position in Category */}
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <p className="text-sm text-gray-600 text-center">
              Exercise {currentExerciseIndex + 1} of {currentCategory.exercises.length} in {currentCategory.name}
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="bg-white border-t border-gray-200 p-4 safe-area-bottom">
        <div className="max-w-2xl mx-auto">
          {/* Skip Section Button */}
          <button
            onClick={handleSkipCategory}
            className="w-full text-center text-sm text-gray-500 hover:text-gray-700 mb-3"
          >
            <SkipForward className="h-4 w-4 inline mr-1" />
            Skip {currentCategory.name}
          </button>

          {/* Navigation Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentCategoryIndex === 0 && currentExerciseIndex === 0}
              className="flex-1"
            >
              <ChevronLeft className="h-5 w-5 mr-1" />
              Previous
            </Button>
            
            <Button
              onClick={handleNext}
              className="flex-1"
            >
              {isLastExercise ? (
                <>
                  <CheckCircle className="h-5 w-5 mr-1" />
                  Complete Warmup
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="h-5 w-5 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}